import {
  MapContainer,
  TileLayer,
  Marker,
  useMap,
  useMapEvents,
} from "react-leaflet";
import {
  Box,
  Button,
  Typography,
  TextField,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import { useState, useEffect, useCallback, useRef } from "react";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import SearchIcon from "@mui/icons-material/Search";
import InputAdornment from "@mui/material/InputAdornment";

// Component to handle map clicks
const ClickHandler = ({ onSelect }) => {
  useMapEvents({
    click(e) {
      onSelect(e.latlng);
    },
  });
  return null;
};

// Component to update map center when position changes
const MapUpdater = ({ lat, lng }) => {
  const map = useMap();

  useEffect(() => {
    if (lat && lng) {
      const numLat = Number(lat);
      const numLng = Number(lng);
      if (!isNaN(numLat) && !isNaN(numLng)) {
        map.setView([numLat, numLng], 16);
        map.invalidateSize();
      }
    }
  }, [lat, lng, map]);

  return null;
};

const LocationPickerMap = ({ formik, isHero }) => {
  const [loadingAddress, setLoadingAddress] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);
  const [addressOptions, setAddressOptions] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [mapKey, setMapKey] = useState(0);
  const debounceTimer = useRef(null);

  // Default position (Karachi)
  const defaultPosition = [24.8607, 67.0011];

  const position =
    formik.values.lat && formik.values.lng
      ? [Number(formik.values.lat), Number(formik.values.lng)]
      : defaultPosition;
  console.log(position, "position");

  // Initialize input value from formik
  useEffect(() => {
    setInputValue(formik.values?.propertyAddress || "");
  }, [formik.values?.propertyAddress]);

  // Reset map key when location is cleared
  useEffect(() => {
    if (!formik.values.lat && !formik.values.lng) {
      setMapKey(0);
    }
  }, [formik.values.lat, formik.values.lng]);

  // 🔍 Search addresses (forward geocoding)
  const searchAddresses = useCallback(async (query) => {
    if (!query || query.length < 1) {
      setAddressOptions([]);
      return;
    }

    try {
      setSearchLoading(true);
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
          `${query}, Karachi, Pakistan`
        )}&limit=6&addressdetails=1`
      );
      const data = await res.json();

      const options = data.map((item) => ({
        label: item.display_name,
        lat: parseFloat(item.lat),
        lng: parseFloat(item.lon),
        address: item.address,
      }));

      setAddressOptions(options);
    } catch (err) {
      setAddressOptions([]);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  // Debounced search
  const handleInputChange = (event, newInputValue) => {
    setInputValue(newInputValue);

    // If input is cleared, also clear the propertyAddress to trigger validation
    if (newInputValue === "") {
      formik.setFieldValue("propertyAddress", "");
      formik.setFieldValue("lat", "");
      formik.setFieldValue("lng", "");
      formik.setFieldValue("mapAddress", "");
    }

    // Clear existing timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Set new timer for debounced search
    debounceTimer.current = setTimeout(() => {
      searchAddresses(newInputValue);
    }, 500);
  };

  // Handle address selection from dropdown
  const handleAddressSelect = (event, selectedOption) => {
    if (selectedOption) {
      // Update lat/lng
      formik.setFieldValue("lat", selectedOption.lat);
      formik.setFieldValue("lng", selectedOption.lng);

      // Update addresses
      formik.setFieldValue("propertyAddress", selectedOption.label);
      formik.setFieldValue("mapAddress", selectedOption.label);

      setInputValue(selectedOption.label);
    }
  };
console.log("inputValue", inputValue);
console.log("addressOptions", addressOptions);
  // 🔁 Reverse Geocode (lat/lng → address)
  const fetchAddress = async (lat, lng, updatePropertyAddress = false) => {
    try {
      setLoadingAddress(true);
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1`
      );
      const data = await res.json();
      const mapAddress = data.display_name || "";
      formik.setFieldValue("mapAddress", mapAddress);

      // Update propertyAddress if forced or if it's empty
      if (updatePropertyAddress || !formik.values?.propertyAddress) {
        formik.setFieldValue("propertyAddress", mapAddress);
        setInputValue(mapAddress);
      }
    } catch (err) {
      // Address fetch failed silently
    } finally {
      setLoadingAddress(false);
    }
  };

  const setLocation = (latlng, updatePropertyAddress = false) => {
    formik.setFieldValue("lat", latlng.lat);
    formik.setFieldValue("lng", latlng.lng);
    fetchAddress(latlng.lat, latlng.lng, updatePropertyAddress);
    //   setTimeout(() => {
    //   document.querySelector('.leaflet-container')?.dispatchEvent(new Event('resize'));
    // }, 100);
  };

  const useCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser");
      return;
    }

    setLocationLoading(true);

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;

        // Update formik values
        formik.setFieldValue("lat", lat);
        formik.setFieldValue("lng", lng);

        // Force map to re-render by changing key
        setMapKey((prev) => prev + 1);

        // Fetch address for this location
        fetchAddress(lat, lng, true);

        setLocationLoading(false);
      },
      (error) => {
        setLocationLoading(false);
        let errorMessage = "Unable to get your location";
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage =
              "Location permission denied. Please allow location access in your browser.";
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = "Location information is unavailable.";
            break;
          case error.TIMEOUT:
            errorMessage = "Location request timed out.";
            break;
          default:
            errorMessage = "An unknown error occurred.";
        }
        alert(errorMessage);
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0,
      }
    );
  };

  return (
    <Box mt={isHero ? 0 : 2}>
      {isHero ? null : (
        <Typography fontWeight={600} mb={1}>
          Property Location
        </Typography>
      )}

      {/* Address Search Autocomplete */}
      <Autocomplete
        freeSolo
        options={addressOptions}
        getOptionLabel={(option) =>
          typeof option === "string" ? option : option.label
        }
        inputValue={inputValue ?? ""}
        onInputChange={handleInputChange}
        onChange={handleAddressSelect}
        loading={searchLoading}
        filterOptions={(x) => x} // Disable built-in filtering since we're using API
        noOptionsText={
          inputValue?.length < 2
            ? "Type at least 2 characters..."
            : "No addresses found"
        }
        renderInput={(params) => (
          <TextField
            {...params}
            name="propertyAddress"
            size="small"
            label={isHero ? 'Location' : "Search Property Address"}
            placeholder="Type address to search..."
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <>
                  {searchLoading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            error={
              formik.touched?.propertyAddress &&
              Boolean(formik.errors?.propertyAddress)
            }
            helperText={
              formik.touched?.propertyAddress && formik.errors?.propertyAddress
            }
          />
        )}
        renderOption={(props, option) => (
          <li {...props} key={option.lat + option.lng}>
            <Box>
              <Typography variant="body2" fontWeight={500}>
                {option.label.split(",")[0]}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {option.label.split(",").slice(1).join(",")}
              </Typography>
            </Box>
          </li>
        )}
        sx={{ mb: isHero ? 0 : 2 }}
      />

      {isHero ? null : (
        <Button
          size="small"
          variant="outlined"
          onClick={useCurrentLocation}
          disabled={locationLoading}
          startIcon={
            locationLoading ? (
              <CircularProgress size={18} />
            ) : (
              <MyLocationIcon />
            )
          }
          sx={{ mb: 1 }}
        >
          {locationLoading ? "Getting Location..." : "Use Current Location"}
        </Button>
      )}

      {!isHero && (formik.values.lat || formik.values.lng) && inputValue?.length > 1 && (
        <MapContainer
          key={mapKey}
          center={position}
          zoom={14}
          style={{ height: 280, width: "100%", borderRadius: 8 }}
        >
          <TileLayer
            attribution="&copy; OpenStreetMap contributors"
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          <Marker
            position={position}
            draggable
            eventHandlers={{
              dragend: (e) => {
                const latlng = e.target.getLatLng();
                // Update property address when marker is dragged
                setLocation(latlng, true);
              },
            }}
          />

          <ClickHandler onSelect={(latlng) => setLocation(latlng, true)} />
          <MapUpdater lat={formik.values.lat} lng={formik.values.lng} />
        </MapContainer>
      )}

      {/* 📍 Address Display */}
      {isHero ? null : (
        <>
          <Box mt={1.5} p={1.5} bgcolor="#f5f5f5" borderRadius={2}>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              Selected Location:
            </Typography>

            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {loadingAddress
                ? "Fetching address..."
                : formik.values.mapAddress ||
                  "Click on map or search address above"}
            </Typography>

            {formik.values.lat && formik.values.lng && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 0.5 }}
              >
                Coordinates: {Number(formik.values.lat).toFixed(6)},{" "}
                {Number(formik.values.lng).toFixed(6)}
              </Typography>
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

export default LocationPickerMap;
